import { Button, Checkbox, Empty, Input, Radio, Select, Space, Typography } from "antd";

import {
  CampoTelefono,
  SelectorDiscapacidad,
  SelectorIdioma,
  SelectorModalidad,
  SelectorNivelFormacion,
  SelectorNivelIdioma,
  SelectorPais,
} from "../../components/fields/campos";
import {
  categoriasActivas,
  PREGUNTAS_INCLUSION,
  type DetallesDiscapacidad,
  type PreguntaInclusion,
  type RespuestaInclusion,
} from "../../lib/document/contextoInclusion";
import type { CVDocument } from "../../lib/document/model";
import { leerRuta, parsearRuta, type Ruta } from "../../lib/document/paths";
import type { Accion } from "./useDocumento";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * El panel que se abre al lado del documento.
 *
 * No hay navegacion: la seleccion es un texto (`experience[1].details`) y este
 * componente decide que editor mostrar a partir de la RAIZ de esa ruta. Por eso
 * añadir una seccion nueva no obliga a tocar el documento ni el panel entre si:
 * solo hay que enseñarle a este que hacer con una raiz mas.
 *
 * Los campos de vocabulario cerrado (teléfono, país, idioma, nivel) viven en
 * components/fields: el panel decide QUÉ editor abrir, no cómo se pinta cada
 * campo.
 */

interface Props {
  doc: CVDocument;
  ruta: Ruta | null;
  editar: (ruta: Ruta, valor: unknown) => void;
  despachar: (a: Accion) => void;
  onCerrar: () => void;
  /** Abre el editor de otra ruta, como si se hubiera pulsado en el documento. */
  onIr: (ruta: Ruta) => void;
}

const Campo = ({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) => (
  <label style={{ display: "block", marginBottom: 12 }}>
    <Text type="secondary" style={{ fontSize: 12.5, display: "block", marginBottom: 4 }}>
      {etiqueta}
    </Text>
    {children}
  </label>
);

/** Lista de textos editable como etiquetas: competencias, herramientas. */
const EditorEtiquetas = ({
  valores,
  onCambio,
}: {
  valores: string[];
  onCambio: (v: string[]) => void;
}) => (
  <Select
    mode="tags"
    value={valores}
    onChange={onCambio}
    style={{ width: "100%" }}
    placeholder="Escribe y pulsa Enter"
    tokenSeparators={[",", "·"]}
    // Sin opciones: es entrada libre. El catálogo llega con las sugerencias.
    options={[]}
  />
);

/**
 * Una pregunta del contexto de inclusion, con el mismo control que en el
 * registro: radios para una opcion, casillas para varias, y el texto libre de
 * "Otra" solo cuando esta marcada.
 */
const PreguntaContexto = ({
  pregunta,
  respuestas,
  onCambio,
}: {
  pregunta: PreguntaInclusion;
  respuestas: Record<string, RespuestaInclusion>;
  onCambio: (clave: string, valor: RespuestaInclusion) => void;
}) => {
  const valor = respuestas[pregunta.id];

  if (pregunta.type === "text") {
    return (
      <Campo etiqueta={pregunta.prompt}>
        <TextArea
          value={typeof valor === "string" ? valor : ""}
          onChange={(e) => onCambio(pregunta.id, e.target.value)}
          autoSize={{ minRows: 4, maxRows: 12 }}
          placeholder={pregunta.helperText}
        />
      </Campo>
    );
  }

  const marcadas = Array.isArray(valor) ? valor : typeof valor === "string" && valor ? [valor] : [];
  const conTexto = (pregunta.options ?? []).some((o) => o.hasFreeText && marcadas.includes(o.value));
  const opciones = (pregunta.options ?? []).map((o) => ({ value: o.value, label: o.label }));
  const libre = respuestas[`${pregunta.id}OtherText`];

  return (
    <Campo etiqueta={pregunta.prompt}>
      {pregunta.type === "single" ? (
        <Radio.Group
          className="panel-contexto-opciones"
          value={marcadas[0]}
          onChange={(e) => onCambio(pregunta.id, e.target.value)}
          options={opciones}
        />
      ) : (
        <Checkbox.Group
          className="panel-contexto-opciones"
          value={marcadas}
          onChange={(v) => onCambio(pregunta.id, v as string[])}
          options={opciones}
        />
      )}
      {conTexto && (
        <Input
          style={{ marginTop: 6 }}
          placeholder="Cuéntanos más"
          value={typeof libre === "string" ? libre : ""}
          onChange={(e) => onCambio(`${pregunta.id}OtherText`, e.target.value)}
        />
      )}
    </Campo>
  );
};

export const PanelEdicion = ({ doc, ruta, editar, despachar, onCerrar, onIr }: Props) => {
  if (!ruta) {
    return (
      <div style={{ padding: "48px 20px" }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              Haz clic en cualquier parte de tu hoja de vida
              <br />
              para editarla aquí.
            </span>
          }
        />
      </div>
    );
  }

  const segmentos = parsearRuta(ruta);
  const raiz = segmentos[0]?.clave ?? "";
  const indice = segmentos[0]?.indice;
  const hoja = segmentos[1]?.clave;

  const cabecera = (titulo: string, alEliminar?: () => void) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        marginBottom: 16,
      }}
    >
      <Text strong style={{ fontSize: 15 }}>
        {titulo}
      </Text>
      <Space size={4}>
        {alEliminar && (
          <Button size="small" danger type="text" onClick={alEliminar}>
            Eliminar
          </Button>
        )}
        <Button size="small" type="text" onClick={onCerrar}>
          Cerrar
        </Button>
      </Space>
    </div>
  );

  // --- contacto ---
  if (["name", "last_names", "email", "phone", "city", "links"].includes(raiz)) {
    return (
      <div>
        {cabecera("Datos personales")}
        <Campo etiqueta="Nombres">
          <Input value={doc.name} onChange={(e) => editar("name", e.target.value)} />
        </Campo>
        <Campo etiqueta="Apellidos">
          <Input value={doc.last_names} onChange={(e) => editar("last_names", e.target.value)} />
        </Campo>
        <Campo etiqueta="Correo">
          <Input value={doc.email} onChange={(e) => editar("email", e.target.value)} />
        </Campo>
        <Campo etiqueta="Teléfono">
          <CampoTelefono value={doc.phone} onChange={(v) => editar("phone", v)} />
        </Campo>
        <Campo etiqueta="Ciudad">
          <Input value={doc.city} onChange={(e) => editar("city", e.target.value)} />
        </Campo>
        <Checkbox
          checked={doc.open_to_remote}
          onChange={(e) => editar("open_to_remote", e.target.checked)}
          style={{ marginBottom: 12 }}
        >
          Abierto a trabajo remoto
        </Checkbox>
        <Campo etiqueta="Enlaces de perfil">
          <EditorEtiquetas valores={doc.links} onCambio={(v) => editar("links", v)} />
        </Campo>

        {/* El tipo, el certificado y las preguntas viven en su propio panel:
            aquí solo el acceso, para que no haya dos sitios donde editarlos. */}
        {doc.is_inclusive && (
          <div className="panel-inclusiva">
            <Text strong style={{ display: "block", marginBottom: 4 }}>
              HV inclusiva
            </Text>
            <Text type="secondary" style={{ display: "block", fontSize: 12.5, marginBottom: 8 }}>
              El tipo de discapacidad y tu contexto de inclusión se editan en su propia sección.
            </Text>
            <Button size="small" onClick={() => onIr("disability_details")}>
              Editar contexto de inclusión
            </Button>
          </div>
        )}
      </div>
    );
  }

  // --- contexto de inclusion ---
  // Solo aparece con la HV inclusiva activa, y todo es opcional: el símbolo ya
  // comunica lo esencial, y detallar la condición es decisión de la persona.
  if (raiz === "disability_details") {
    const detalles: DetallesDiscapacidad = doc.disability_details ?? {};
    const categorias = categoriasActivas(doc.disability_type, detalles);
    const responder = (categoria: string, clave: string, valor: RespuestaInclusion) =>
      editar("disability_details", {
        ...detalles,
        [categoria]: { ...(detalles[categoria] ?? {}), [clave]: valor },
      });

    return (
      <div>
        {cabecera("Contexto de inclusión")}
        <Text type="secondary" style={{ display: "block", fontSize: 12.5, marginBottom: 12 }}>
          Lo que respondas aparece al final de tu hoja de vida. Todo es opcional: comparte solo lo
          que quieras que la empresa sepa.
        </Text>
        <Campo etiqueta="Tipo de discapacidad">
          <SelectorDiscapacidad
            value={doc.disability_type}
            onChange={(v) => editar("disability_type", v)}
          />
        </Campo>
        <Checkbox
          checked={doc.disability_certificate}
          onChange={(e) => editar("disability_certificate", e.target.checked)}
          style={{ marginBottom: 16 }}
        >
          Tengo certificado de discapacidad vigente
        </Checkbox>

        {categorias.length === 0 && (
          <Text type="secondary" style={{ display: "block", fontSize: 12.5 }}>
            Elige el tipo de discapacidad para ver sus preguntas.
          </Text>
        )}
        {categorias.map((categoria) => (
          <div className="panel-contexto-bloque" key={categoria}>
            {categorias.length > 1 && (
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                {categoria}
              </Text>
            )}
            {PREGUNTAS_INCLUSION[categoria]!.questions.map((pregunta) => (
              <PreguntaContexto
                key={pregunta.id}
                pregunta={pregunta}
                respuestas={detalles[categoria] ?? {}}
                onCambio={(clave, valor) => responder(categoria, clave, valor)}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }

  // --- perfil ---
  if (raiz === "resume") {
    return (
      <div>
        {cabecera("Perfil profesional")}
        <TextArea
          value={doc.resume}
          onChange={(e) => editar("resume", e.target.value)}
          autoSize={{ minRows: 8, maxRows: 20 }}
          placeholder="Qué haces, cuántos años llevas y en qué eres bueno."
        />
      </div>
    );
  }

  // --- experiencia ---
  if (raiz === "experience" && indice !== undefined) {
    const exp = doc.experience[indice];
    if (!exp) return null;
    const en = (campo: string) => `experience[${indice}].${campo}`;

    // Se abrio pulsando directamente las funciones o los logros: se muestra
    // solo ese campo, que es lo que la persona venia a cambiar.
    if (hoja === "details" || hoja === "achievements") {
      const esDetalle = hoja === "details";
      return (
        <div>
          {cabecera(esDetalle ? "Funciones" : "Logros")}
          <Text type="secondary" style={{ fontSize: 12.5, display: "block", marginBottom: 8 }}>
            {exp.position} · {exp.company_name}
          </Text>
          <TextArea
            value={esDetalle ? exp.details : exp.achievements}
            onChange={(e) => editar(en(hoja), e.target.value)}
            autoSize={{ minRows: 8, maxRows: 22 }}
            placeholder="Una línea por punto, empezando con un verbo."
          />
        </div>
      );
    }

    return (
      <div>
        {cabecera("Experiencia", () =>
          despachar({ tipo: "experiencia:eliminar", indice }),
        )}
        <Campo etiqueta="Cargo">
          <Input value={exp.position} onChange={(e) => editar(en("position"), e.target.value)} />
        </Campo>
        <Campo etiqueta="Empresa">
          <Input
            value={exp.company_name}
            onChange={(e) => editar(en("company_name"), e.target.value)}
          />
        </Campo>
        <Campo etiqueta="Sector">
          <Input value={exp.sector} onChange={(e) => editar(en("sector"), e.target.value)} />
        </Campo>
        <Space style={{ width: "100%" }} size={8}>
          <Campo etiqueta="Desde (AAAA-MM)">
            <Input
              value={exp.start_date ?? ""}
              placeholder="2024-01"
              onChange={(e) => editar(en("start_date"), e.target.value || null)}
              // Al soltar el campo se reordena: hacerlo en cada tecla movería
              // la tarjeta a media escritura.
              onBlur={() => despachar({ tipo: "experiencia:reordenar" })}
            />
          </Campo>
          <Campo etiqueta="Hasta (AAAA-MM)">
            <Input
              value={exp.end_date ?? ""}
              placeholder="2025-06"
              disabled={exp.current_job}
              onChange={(e) => editar(en("end_date"), e.target.value || null)}
              onBlur={() => despachar({ tipo: "experiencia:reordenar" })}
            />
          </Campo>
        </Space>
        <Checkbox
          checked={exp.current_job}
          onChange={(e) => {
            editar(en("current_job"), e.target.checked);
            if (e.target.checked) editar(en("end_date"), null);
            despachar({ tipo: "experiencia:reordenar" });
          }}
          style={{ marginBottom: 12 }}
        >
          Es mi trabajo actual
        </Checkbox>
        <Campo etiqueta="Funciones">
          <TextArea
            value={exp.details}
            onChange={(e) => editar(en("details"), e.target.value)}
            autoSize={{ minRows: 4, maxRows: 14 }}
          />
        </Campo>
        <Campo etiqueta="Logros">
          <TextArea
            value={exp.achievements}
            onChange={(e) => editar(en("achievements"), e.target.value)}
            autoSize={{ minRows: 3, maxRows: 12 }}
          />
        </Campo>
      </div>
    );
  }

  // --- educacion ---
  if (raiz === "education" && indice !== undefined) {
    const est = doc.education[indice];
    if (!est) return null;
    const en = (campo: string) => `education[${indice}].${campo}`;

    return (
      <div>
        {cabecera("Estudio", () => despachar({ tipo: "estudio:eliminar", indice }))}
        <Campo etiqueta="Título">
          <Input value={est.title_name} onChange={(e) => editar(en("title_name"), e.target.value)} />
        </Campo>
        <Campo etiqueta="Institución">
          <Input
            value={est.institution_name}
            onChange={(e) => editar(en("institution_name"), e.target.value)}
          />
        </Campo>
        <Campo etiqueta="Nivel de formación">
          <SelectorNivelFormacion
            value={est.formation_level}
            onChange={(v) => editar(en("formation_level"), v ?? "")}
          />
        </Campo>
        <Space size={8}>
          <Campo etiqueta="Desde">
            <Input
              value={est.start_year}
              placeholder="2019"
              onChange={(e) => editar(en("start_year"), e.target.value)}
            />
          </Campo>
          <Campo etiqueta="Hasta">
            <Input
              value={est.year}
              placeholder="2023"
              disabled={est.is_current}
              onChange={(e) => editar(en("year"), e.target.value)}
            />
          </Campo>
        </Space>
        <Checkbox
          checked={est.is_current}
          onChange={(e) => editar(en("is_current"), e.target.checked)}
          style={{ marginBottom: 12 }}
        >
          En curso
        </Checkbox>
        <Campo etiqueta="Modalidad">
          <SelectorModalidad value={est.format} onChange={(v) => editar(en("format"), v ?? "")} />
        </Campo>
        <Campo etiqueta="País">
          <SelectorPais
            value={est.country_name}
            onChange={(v) => editar(en("country_name"), v ?? "")}
          />
        </Campo>
      </div>
    );
  }

  // --- listas de competencias ---
  if (raiz === "skills" || raiz === "soft_skills" || raiz === "tools") {
    const titulos: Record<string, string> = {
      skills: "Competencias",
      soft_skills: "Habilidades blandas",
      tools: "Herramientas",
    };
    return (
      <div>
        {cabecera(titulos[raiz] ?? raiz)}
        <EditorEtiquetas
          valores={(leerRuta(doc, raiz) as string[]) ?? []}
          onCambio={(v) => editar(raiz, v)}
        />
      </div>
    );
  }

  // --- idiomas ---
  if (raiz === "languages") {
    return (
      <div>
        {cabecera("Idiomas")}
        {doc.languages.map((idioma, i) => (
          <Space key={idioma.id} style={{ display: "flex", marginBottom: 8 }} size={6}>
            <div style={{ width: 170 }}>
              <SelectorIdioma
                value={idioma.name}
                onChange={(v) => editar(`languages[${i}].name`, v ?? "")}
              />
            </div>
            <div style={{ width: 130 }}>
              <SelectorNivelIdioma
                value={idioma.level}
                onChange={(v) => editar(`languages[${i}].level`, v ?? "")}
              />
            </div>
            <Button type="text" danger onClick={() => despachar({ tipo: "idioma:eliminar", indice: i })}>
              ×
            </Button>
          </Space>
        ))}
        <Button size="small" onClick={() => despachar({ tipo: "idioma:añadir" })}>
          Añadir idioma
        </Button>
      </div>
    );
  }

  // --- otros ---
  if (raiz === "others" && indice !== undefined) {
    const otro = doc.others[indice];
    if (!otro) return null;
    return (
      <div>
        {cabecera("Otra sección", () => despachar({ tipo: "otro:eliminar", indice }))}
        <Campo etiqueta="Título">
          <Input
            value={otro.section_name}
            onChange={(e) => editar(`others[${indice}].section_name`, e.target.value)}
          />
        </Campo>
        <Campo etiqueta="Contenido">
          <TextArea
            value={otro.section_text}
            onChange={(e) => editar(`others[${indice}].section_text`, e.target.value)}
            autoSize={{ minRows: 5, maxRows: 14 }}
          />
        </Campo>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      {cabecera("Sin editor")}
      <Text type="secondary">Todavía no hay un editor para «{ruta}».</Text>
    </div>
  );
};
